import { useState, useCallback } from 'react';
import { getInstrument, getAllInstruments } from '../data/InstrumentRegistry';
import { GENRE_LIBRARY } from '../data/GenreLibrary';

// Helper to fuzz values
const fuzz = (val, variance) => {
    return val + ((Math.random() * variance * 2) - variance);
};

export const useEnsemble = (initialEnsemble = []) => {
    // State: List of active instrument objects
    // Each object has { ...definition, instanceId, muted, soloed, delayEnabled, volume, arrangement: { intro: true, ... } }
    const [activeInstruments, setActiveInstruments] = useState(initialEnsemble);

    const defaultArrangement = {
        intro: true,
        verse: true,
        chorus: true,
        bridge: true,
        outro: true
    };

    const addInstrument = useCallback((id) => {
        const def = getInstrument(id);
        if (!def) return;

        setActiveInstruments(prev => {
            const newInst = {
                ...def,
                instanceId: `${def.id}_${Date.now()}_${Math.floor(Math.random()*1000)}`,
                muted: false,
                soloed: false,
                delayEnabled: false, // Default delay OFF
                volume: -15, // Lowered to prevent clipping
                arrangement: { ...defaultArrangement }
            };
            return [...prev, newInst];
        });
    }, []);

    const removeInstrument = useCallback((instanceId) => {
        setActiveInstruments(prev => prev.filter(inst => inst.instanceId !== instanceId));
    }, []);

    const toggleMute = useCallback((instanceId) => {
        setActiveInstruments(prev => prev.map(inst =>
            inst.instanceId === instanceId ? { ...inst, muted: !inst.muted } : inst
        ));
    }, []);

    const toggleSolo = useCallback((instanceId) => {
        setActiveInstruments(prev => {
            const target = prev.find(i => i.instanceId === instanceId);
            if (!target) return prev;

            const isSoloing = !target.soloed;

            if (isSoloing) {
                return prev.map(inst =>
                    inst.instanceId === instanceId ? { ...inst, soloed: true } : inst
                );
            } else {
                return prev.map(inst =>
                    inst.instanceId === instanceId ? { ...inst, soloed: false } : inst
                );
            }
        });
    }, []);

    const toggleDelay = useCallback((instanceId) => {
        setActiveInstruments(prev => prev.map(inst =>
            inst.instanceId === instanceId ? { ...inst, delayEnabled: !inst.delayEnabled } : inst
        ));
    }, []);

    const toggleArrangement = useCallback((instanceId, section) => {
        setActiveInstruments(prev => prev.map(inst => {
            if (inst.instanceId !== instanceId) return inst;
            return {
                ...inst,
                arrangement: {
                    ...inst.arrangement,
                    [section]: !inst.arrangement[section]
                }
            };
        }));
    }, []);

    const loadFromIds = useCallback((ids) => {
        if (!Array.isArray(ids)) return;
        const newEnsemble = ids.map((id, idx) => {
            let def = getInstrument(id);
            if (!def) return null;

            return {
                ...def,
                instanceId: `${def.id}_restored_${idx}_${Date.now()}`,
                muted: false,
                soloed: false,
                delayEnabled: false,
                volume: -15,
                arrangement: { ...defaultArrangement }
            };
        }).filter(Boolean);

        if (newEnsemble.length > 0) {
            setActiveInstruments(newEnsemble);
        }
    }, []);

    // Direct injection for Pipeline results (Preserves instanceId)
    const loadInstruments = useCallback((instruments) => {
        if (!Array.isArray(instruments)) return;
        // Ensure they have UI state properties
        const hydrated = instruments.map(inst => ({
            muted: false,
            soloed: false,
            delayEnabled: false,
            volume: -15,
            arrangement: { ...defaultArrangement },
            ...inst // Overwrite defaults if present, crucial for instanceId
        }));
        setActiveInstruments(hydrated);
    }, []);

    // --- PROCEDURAL GENERATOR ---
    const generateEnsembleForGenre = (genreKey, subGenreKey) => {
        const genreDef = GENRE_LIBRARY[genreKey];
        if (!genreDef) return []; // Should filter to defaults

        const subGenreDef = genreDef.subGenres ? genreDef.subGenres[subGenreKey] : null;
        const constraint = genreDef.ensembleConstraint || { min: 3, max: 5, requiredRoles: ['bass', 'melody'] };

        // 1. Determine Target Size
        const targetSize = Math.max(constraint.min, Math.min(constraint.max, Math.round(fuzz((constraint.min + constraint.max) / 2, 1))));

        // 2. Filter Instruments by Tags
        const allInsts = getAllInstruments();
        let validInsts = allInsts;

        // Collect tags from SubGenre (if any) or infer from name
        // e.g. 'indian_raga' -> ['indian']
        let targetTags = [];

        // If subGenreDef exists and has logicTags, try to extract family?
        // Or better, define a mapping or use fuzzy logic on the name.
        if (subGenreKey) {
            const key = subGenreKey.toLowerCase();
            if (key.includes('indian')) targetTags.push('indian');
            if (key.includes('east')) targetTags.push('east-asian');
            if (key.includes('metal')) targetTags.push('metal');
            if (key.includes('rock')) targetTags.push('rock');
            if (key.includes('jazz')) targetTags.push('jazz');
            if (key.includes('polka')) targetTags.push('polka');
            if (key.includes('klezmer')) targetTags.push('klezmer');
            if (key.includes('balkan')) targetTags.push('balkan');
            if (key.includes('electronic') || key.includes('techno') || key.includes('trap')) targetTags.push('electronic');
            if (key.includes('orchestra') || key.includes('cinematic')) targetTags.push('orchestral');
        }

        // If generic top level tags
        if (genreKey === 'orchestra') targetTags.push('orchestral');
        if (genreKey === 'rock_band') targetTags.push('band');
        if (genreKey === 'jazz_combo') targetTags.push('jazz');
        if (genreKey === 'electronic') targetTags.push('electronic');

        if (targetTags.length > 0) {
            validInsts = allInsts.filter(inst => {
                if (!inst.tags) return false;
                return targetTags.some(tag => inst.tags.includes(tag));
            });
        }

        // Fallback if filtering was too aggressive
        if (validInsts.length < constraint.min) {
            console.warn('Ensemble generation: Filters too strict, relaxing...', targetTags);
            // Relax to just top level genre tag
            if (genreKey === 'world') {
                 validInsts = allInsts.filter(inst => inst.category === 'World');
            } else {
                 validInsts = allInsts; // Reset
            }
        }

        const selectedIds = new Set();
        const selectedRoles = new Set();

        // 3. Fill Required Roles
        if (constraint.requiredRoles) {
            constraint.requiredRoles.forEach(role => {
                const candidates = validInsts.filter(inst =>
                    inst.roles && inst.roles.includes(role) && !selectedIds.has(inst.id)
                );

                if (candidates.length > 0) {
                    // Pick one randomly
                    const pick = candidates[Math.floor(Math.random() * candidates.length)];
                    selectedIds.add(pick.id);
                    selectedRoles.add(role);
                }
            });
        }

        // 4. Fill Remaining Spots
        while (selectedIds.size < targetSize) {
            // Prioritize diversity? Or doubling?
            // Orchestra doubles (Violin 1, Violin 2). Bands usually don't double bass.

            const remaining = validInsts.filter(inst => {
                // If bass, only one usually (unless Orchestra)
                if (genreKey !== 'orchestra' && inst.roles.includes('bass') && selectedRoles.has('bass')) return false;
                // If drum kit, only one
                if (inst.id === 'drums' && selectedIds.has('drums')) return false;
                return true;
            });

            if (remaining.length === 0) break; // No more candidates

            const pick = remaining[Math.floor(Math.random() * remaining.length)];

            // Allow duplicate IDs only for Orchestral Strings/Brass?
            // Currently IDs are unique in `selectedIds`.
            // If we want multiple violins, we need unique instance logic, but `selectedIds` is just for lookup.
            // If we re-pick an ID, we just add another instance.
            // But `selectedIds` prevents re-picking.
            // Let's allow re-picking for Orchestra.

            if (!selectedIds.has(pick.id) || genreKey === 'orchestra') {
                 selectedIds.add(pick.id);
                 // If ID exists, we need to push to array, Set won't work for count if unique IDs.
                 // Actually, let's build an array.
            } else {
                // Retry limit?
            }

            // Break loop for safety if stuck
            if (Math.random() > 0.9) break;
        }

        // Convert Set to Array (or just return the Ids found)
        // Since Set doesn't support duplicates easily, let's re-do logic slightly or just accept unique instruments for now
        // except Orchestra where we might manually add section leaders.

        return Array.from(selectedIds);
    };

    const loadPreset = useCallback((presetName, subGenre) => {
        let ids = [];

        // Try procedural generation first
        if (GENRE_LIBRARY[presetName]) {
            ids = generateEnsembleForGenre(presetName, subGenre);
        }

        // Fallback for Legacy/Hardcoded logic if generation failed or returned empty
        if (ids.length === 0) {
            switch (presetName) {
                case 'orchestra':
                case 'full_orchestra':
                    ids = ['flute', 'oboe', 'clarinet', 'bassoon', 'horn', 'trumpet', 'trombone', 'tuba', 'timpani', 'violin_1', 'violin_2', 'viola', 'cello', 'contrabass'];
                    break;
                case 'rock_band':
                    ids = ['drums', 'electric_bass', 'rhythm_guitar', 'lead_guitar'];
                    break;
                case 'jazz_combo':
                    ids = ['drums', 'upright_bass', 'piano', 'trumpet', 'saxophone'];
                    break;
                case 'electronic':
                case 'edm_kit':
                    ids = ['drums', 'bass_drop', 'supersaw_lead', 'glass_pad'];
                    break;
                case 'world':
                case 'world_ensemble':
                    ids = ['timpani', 'sitar', 'koto', 'duduk'];
                    break;
                default:
                    ids = ['piano'];
                    break;
            }
        }

        const newEnsemble = ids.map((id, idx) => {
            let def = getInstrument(id);
            // Fallback for Saxophone if missing
            if (!def && id === 'saxophone') {
                 def = getInstrument('oboe'); // Temporary hack if Sax not in DB
            }

            if (!def) return null;

            return {
                ...def,
                instanceId: `${def.id}_preset_${idx}_${Date.now()}`,
                muted: false,
                soloed: false,
                delayEnabled: false,
                volume: -15,
                arrangement: { ...defaultArrangement }
            };
        }).filter(Boolean);

        setActiveInstruments(newEnsemble);
    }, []);

    return {
        activeInstruments,
        addInstrument,
        removeInstrument,
        toggleMute,
        toggleSolo,
        toggleDelay,
        toggleArrangement,
        loadPreset,
        loadFromIds,
        loadInstruments
    };
};
